import React from 'react';
import styled from 'styled-components';
// import { ColorProperty } from "csstype";
import { omit } from '../../utils';

// We could use an enum here but then in order to specify the size value,
// the consumer would have to import the enum along with the component.
const SpinnerSizes = {
  xs: '0.75rem',
  sm: '1.5rem',
  md: '2.5rem',
  lg: '4rem',
  xl: '130px',
};


export interface Spinner {
  color?: any;
  size?: 'xs' | 'sm' | 'md' | 'lg' | 'xl';
  className?: string;
}

const getSize = (size: string) => SpinnerSizes[size] || size;

/**
 * Use the `Spinner` to inform users about the status of ongoing processes, such as loading an app, submitting a form, or saving updates.
 * It's best used inside buttons, search dropdowns, and in place of the `LoadingIndicator` when you don't wish to display a message to the user.
 */
export const Spinner = (props: Spinner) => {
  const { size, color } = props;
  return (
    <SpinnerContent size={getSize(size)} {...omit(props, ['size', 'color'])}>
      <CircleWrapper viewBox="0 0 100 100" color={color}>
          <circle className="path" cx="50%" cy="50%" r="46" />
      </CircleWrapper>
    </SpinnerContent>
)};

Spinner.defaultProps = {
  color: '#009ac7',
  size: 'xl',
}

export default Spinner;

const SpinnerContent = styled.div<Spinner>`
    position: relative;
    width: ${props => props.size};
    height: ${props => props.size};
    transform: rotate(-90deg);
    animation: spinner-rotate 1.5s linear infinite;
    overflow: visible;

    @keyframes spinner-rotate {
      0% {
        transform: rotate(0)
      }
      100% {
        transform: rotate(360deg)
      }
    }
    @keyframes stroke-rotate {
      0% {
        stroke-dashoffset: 268.60617px;
        transform: rotate(0);
      }
      12.5% {
        stroke-dashoffset: 56.54867px;
        transform: rotate(0);
      }
      12.5001% {
        stroke-dashoffset: 56.54867px;
        transform: rotateX(180deg) rotate(72.5deg);
      }
      25% {
        stroke-dashoffset: 268.60617px;
        transform: rotateX(180deg) rotate(72.5deg);
      }
      25.0001% {
        stroke-dashoffset: 268.60617px;
        transform: rotate(270deg);
      }
      37.5% {
        stroke-dashoffset: 56.54867px;
        transform: rotate(270deg);
      }
      37.5001% {
        stroke-dashoffset: 56.54867px;
        transform: rotateX(180deg) rotate(161.5deg);
      }
      50% {
        stroke-dashoffset: 268.60617px;
        transform: rotateX(180deg) rotate(161.5deg);
      }
      50.0001% {
        stroke-dashoffset: 268.60617px;
        transform: rotate(180deg);
      }
      62.5% {
        stroke-dashoffset: 56.54867px;
        transform: rotate(180deg);
      }
      62.5001% {
        stroke-dashoffset: 56.54867px;
        transform: rotateX(180deg) rotate(251.5deg);
      }
      75% {
        stroke-dashoffset: 268.60617px;
        transform: rotateX(180deg) rotate(251.5deg);
      }
      75.0001% {
        stroke-dashoffset: 268.60617px;
        transform: rotate(90deg);
      }
      87.5% {
        stroke-dashoffset: 56.54867px;
        transform: rotate(90deg);
      }
      87.5001% {
        stroke-dashoffset: 56.54867px;
        transform: rotateX(180deg) rotate(341.5deg);
      }
      100% {
        stroke-dashoffset: 268.60617px;
        transform: rotateX(180deg) rotate(341.5deg);
      }
    }
`;
const isIE11 = !!window.MSInputMethodContext && !!(document as any).documentMode;

const CircleWrapper = styled.svg`
    position: absolute;
    transform: rotate(-90deg);
    top: 0;
    left: 0;
    & .path {
        animation: stroke-rotate 4s cubic-bezier(.35,0,.25,1) infinite backwards, fadeIn .25s ease 1.65s forwards;
        stroke-dasharray:  ${isIE11 ? `250, 200` : `282.743px`};
        stroke-width: 4;
        transition-property: stroke;
        fill: transparent;
        transform-origin: center;
        transition: stroke-dashoffset 225ms linear;
        stroke: ${props => props.color};
    }
`;